import { motion, AnimatePresence } from "framer-motion";
import { X, Star, Bookmark, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { MovieData } from "./SwipeCard";

interface SessionListPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon: "star" | "bookmark";
  items: MovieData[];
  onRemove: (id: number) => void;
}

const SessionListPanel = ({ open, onClose, title, icon, items, onRemove }: SessionListPanelProps) => {
  const { t } = useTranslation();
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-x-4 top-14 z-30 rounded-2xl border border-border bg-card/85 backdrop-blur-xl shadow-card overflow-hidden"
        >
          <div className="flex items-center justify-between p-3 border-b border-border/50">
            <div className="flex items-center gap-2">
              {icon === "star" ? (
                <Star className="w-4 h-4 text-star fill-star" />
              ) : (
                <Bookmark className="w-4 h-4 text-bookmark fill-bookmark" />
              )}
              <h3 className="font-display text-sm font-bold text-foreground">{title}</h3>
              <span className="text-[10px] text-muted-foreground">({items.length})</span>
            </div>
            <button onClick={onClose} className="w-6 h-6 rounded-full bg-secondary/80 flex items-center justify-center">
              <X className="w-3 h-3 text-muted-foreground" />
            </button>
          </div>
          <div className="overflow-y-auto max-h-[50vh] p-2 space-y-1.5">
            {items.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">{t("sessionList.nothingHere")}</p>
            ) : (
              items.map((movie) => (
                <div key={movie.id} className="flex items-center gap-2.5 p-2 rounded-xl bg-secondary/40">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    className="w-10 rounded-lg object-cover flex-shrink-0"
                    style={{ height: 60 }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{movie.title}</p>
                    <p className="text-[10px] text-muted-foreground">{movie.year} · ⭐ {movie.rating}</p>
                  </div>
                  <button
                    onClick={() => onRemove(movie.id)}
                    className="p-1.5 rounded-lg bg-destructive/10 hover:bg-destructive/20 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </button>
                </div>
              ))
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SessionListPanel;
